import type { AnatomyBinding, AnatomyVariant } from "./componentAnatomy";

const border = (token: `--${string}`): AnatomyBinding => ({
    partId: "border",
    property: "border-color",
    token,
    via: "--input-border",
});
const text = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color",
    token,
    via: "--input-fg",
});
const placeholder = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color (placeholder)",
    token,
    via: "--input-placeholder",
});
const background = (token: `--${string}`): AnatomyBinding => ({
    partId: "background",
    property: "background-color",
    token,
    via: "--input-bg",
});

const ringBindings: readonly AnatomyBinding[] = [
    {
        partId: "focusRing",
        property: "box-shadow (spread)",
        token: "--border-width-thick",
    },
    {
        partId: "focusRing",
        property: "box-shadow (color)",
        token: "--focus-ring-color",
    },
];

const focusBindings: readonly AnatomyBinding[] = [
    border("--border-accent-focus"),
    ...ringBindings,
];

const primaryRestingBindings: readonly AnatomyBinding[] = [
    background("--bg-primary"),
    text("--fg-primary"),
    placeholder("--fg-tertiary"),
    border("--border-default"),
];

const primaryReadOnlyBindings: readonly AnatomyBinding[] = [
    background("--bg-primary"),
    text("--fg-tertiary"),
    placeholder("--fg-tertiary"),
    border("--border-disabled"),
];

export const inputVariantSpecs: readonly AnatomyVariant[] = [
    {
        id: "primary",
        label: "Primary",
        selector: ".primary",
        restingBindings: primaryRestingBindings,
        stateBindings: {
            hover: [border("--border-hover")],
            focus: focusBindings,
            readOnly: primaryReadOnlyBindings,
            disabled: [
                text("--fg-disabled"),
                placeholder("--fg-disabled"),
                border("--border-disabled"),
            ],
        },
    },
    {
        id: "primary-invalid",
        label: "Primary · invalid",
        selector:
            '.primary:has(.control[aria-invalid="true"]):not(:has(.control:disabled)):not([data-disabled]):not(:has(.control:focus)):not([data-forced-state="focus"])',
        restingBindings: [
            background("--bg-primary"),
            text("--fg-error"),
            placeholder("--fg-error-placeholder"),
            border("--border-error"),
        ],
        stateBindings: {
            hover: [border("--border-error-hover")],
            focus: [...primaryRestingBindings, ...focusBindings],
            readOnly: primaryReadOnlyBindings,
        },
    },
    {
        id: "ghost",
        label: "Ghost",
        selector: ".ghost",
        restingBindings: [
            {
                partId: "background",
                property: "background-color",
                literal: "transparent",
                via: "--input-bg",
            },
            text("--fg-primary"),
            placeholder("--fg-tertiary"),
            {
                partId: "border",
                property: "border-color",
                literal: "transparent",
                via: "--input-border",
                note: "1px kept so hover never shifts content (spec Q10).",
            },
        ],
        stateBindings: {
            hover: [background("--bg-primary"), border("--border-hover")],
            focus: [background("--bg-primary"), ...focusBindings],
            readOnly: primaryReadOnlyBindings,
            disabled: [text("--fg-disabled"), placeholder("--fg-disabled")],
        },
    },
    {
        id: "accent",
        label: "Accent",
        selector: ".accent",
        restingBindings: [
            background("--bg-primary"),
            text("--fg-accent"),
            placeholder("--fg-accent-placeholder"),
            border("--border-accent"),
        ],
        stateBindings: {
            hover: [border("--border-accent-hover")],
            focus: focusBindings,
            readOnly: [
                background("--bg-primary"),
                text("--fg-accent-placeholder"),
                placeholder("--fg-accent-placeholder"),
                border("--border-accent-disabled"),
            ],
            disabled: [
                text("--fg-accent-disabled"),
                placeholder("--fg-accent-disabled"),
                border("--border-accent-disabled"),
            ],
        },
    },
];
